import { useEffect, useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Mic, MicOff, Video as VideoIcon, VideoOff, MonitorSmartphone, Loader2, CheckCircle2, XCircle, Play, Terminal, Trash2, Zap, Gauge } from 'lucide-react'
import { ConsoleEvent } from '@/lib/test-demo/scenarios'
import { MediaResult, MediaSession, runAudioScenario, runScreenScenario, runVideoScenario } from '@/lib/test-demo/media-scenarios'
import { InboundTrackStats } from '@/lib/test-demo/peer'

type Mode = 'audio' | 'video' | 'screen'

interface Props {
  basePath: string
  push: (event: Omit<ConsoleEvent, 'ts'>) => void
}

interface ActiveTest {
  mode: Mode
  session: MediaSession
  result: MediaResult
  startedAt: number
}

interface LiveRtt {
  alice: { rttMs: number | null; localCandidate?: string; remoteCandidate?: string }
  bob: { rttMs: number | null; localCandidate?: string; remoteCandidate?: string }
}

function summarizeLive(stats: InboundTrackStats[], elapsedSeconds: number): MediaResult['stats']['aliceRx'] {
  return stats.map((s) => ({
    kind: s.kind,
    packets: s.packetsReceived,
    frames: s.framesDecoded,
    fps: s.framesPerSecond,
    resolution: s.frameWidth && s.frameHeight ? `${s.frameWidth}x${s.frameHeight}` : undefined,
    kbps: elapsedSeconds > 0 ? Math.round((s.bytesReceived * 8) / 1000 / elapsedSeconds) : 0,
  }))
}

const levelColor: Record<ConsoleEvent['level'], string> = {
  info: 'text-zinc-400',
  request: 'text-cyan-400',
  response: 'text-emerald-400',
  success: 'text-emerald-400',
  error: 'text-red-400',
}

function formatTime(ts: number): string {
  const d = new Date(ts)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}.${String(d.getMilliseconds()).padStart(3, '0')}`
}

function MediaConsole({ events, onClear }: { events: ConsoleEvent[]; onClear: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    ref.current?.scrollTo({ top: ref.current.scrollHeight })
  }, [events.length])

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Terminal className="h-3.5 w-3.5" />
          <span className="uppercase tracking-wider">Media test console</span>
          <span className="font-mono">({events.length})</span>
        </div>
        {events.length > 0 ? (
          <Button variant="ghost" size="sm" onClick={onClear} className="h-7 text-xs">
            <Trash2 className="h-3 w-3 mr-1.5" />
            Clear
          </Button>
        ) : null}
      </div>
      <div ref={ref} className="font-mono text-xs leading-relaxed bg-zinc-950 rounded-lg p-3 h-[240px] overflow-y-auto">
        {events.length === 0 ? (
          <div className="text-zinc-500">Run a media test to see live request/response and stats events here.</div>
        ) : (
          events.map((event, i) => (
            <div key={i} className="mb-1.5">
              <span className="text-zinc-600 mr-2">[{formatTime(event.ts)}]</span>
              <span className={levelColor[event.level]}>{event.text}</span>
              {event.detail ? <pre className="text-zinc-500 ml-12 mt-1 whitespace-pre-wrap break-all">{event.detail}</pre> : null}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

function ConnectTimeHero({ result }: { result: MediaResult }) {
  if (!result.ok || result.connectMs == null) return null
  const ms = result.connectMs
  const subSecond = ms < 1000
  return (
    <div className="surface-card relative overflow-hidden p-5">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at top right, hsl(var(--signal-live) / 0.15) 0%, transparent 65%)',
        }}
      />
      <div className="relative space-y-1">
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <Zap className="h-3.5 w-3.5" />
          Connect time
        </div>
        <div className="flex items-baseline gap-2">
          <div className="font-heading text-3xl font-semibold tabular-nums">
            {ms.toLocaleString()}
            <span className="ml-1 text-base font-normal text-muted-foreground">ms</span>
          </div>
          {subSecond ? (
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-600 dark:text-emerald-400">
              sub-second
            </Badge>
          ) : null}
        </div>
        <div className="text-xs text-muted-foreground">
          From join to first decoded frame on both peers — full WebRTC handshake (REST signaling + SDP + ICE/DTLS + first SRTP frame).
          Excludes the lobby pre-warm (device capture + TURN credentials), which runs before the call starts — the same preview
          pattern Agora and 100ms use.
        </div>
        <div className="mt-3 text-[10px] text-muted-foreground">
          <span className="font-medium">Demo overhead:</span> this number includes 2 peers in 1 tab going through the public sandbox proxy (browser → CDN → server → CDN → API). A real customer client would skip the proxy and run a single peer — expect roughly half this.
        </div>
      </div>
    </div>
  )
}

function TimelineCard({ timeline, totalMs }: { timeline: MediaResult['timeline']; totalMs: number }) {
  if (timeline.length === 0) return null
  const max = timeline.reduce((m, s) => Math.max(m, s.ms), 0)
  return (
    <div className="surface-card space-y-2 p-4">
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        Setup timeline
      </div>
      <div className="space-y-1.5">
        {timeline.map((step, i) => (
          <div key={i} className="text-xs">
            <div className="mb-1 flex justify-between">
              <span>{step.label}</span>
              <span className="tabular-nums text-muted-foreground">{step.ms} ms</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-foreground/[0.06]">
              <div
                className="h-full bg-foreground/60"
                style={{ width: max > 0 ? `${Math.max(2, (step.ms / max) * 100)}%` : '0%' }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between pt-2 text-xs">
        <span className="text-muted-foreground">Total</span>
        <span className="font-mono tabular-nums">{totalMs} ms</span>
      </div>
    </div>
  )
}

function rttColor(ms: number | null): string {
  if (ms == null) return 'text-muted-foreground'
  if (ms < 100) return 'text-emerald-600 dark:text-emerald-400'
  if (ms < 250) return 'text-amber-600 dark:text-amber-400'
  return 'text-red-600 dark:text-red-400'
}

function RttCard({ live }: { live: LiveRtt }) {
  const aliceMs = live.alice.rttMs
  const bobMs = live.bob.rttMs
  const both = [aliceMs, bobMs].filter((v): v is number => typeof v === 'number')
  const min = both.length > 0 ? Math.min(...both) : null
  const subHundred = typeof min === 'number' && min < 100

  return (
    <div className="surface-card space-y-2 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <Gauge className="h-3.5 w-3.5" />
          Live latency to SFU
        </div>
        {subHundred ? (
          <Badge variant="outline" className="border-emerald-500/40 text-emerald-600 dark:text-emerald-400 text-xs">
            &lt; 100 ms
          </Badge>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-3 pt-1">
        <div>
          <div className="text-xs text-muted-foreground">Alice ↔ SFU</div>
          <div className={`text-xl font-semibold tabular-nums ${rttColor(aliceMs)}`}>
            {aliceMs ?? '—'}
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              {aliceMs != null ? 'ms' : ''}
            </span>
          </div>
          {live.alice.remoteCandidate ? (
            <div className="truncate font-mono text-[10px] text-muted-foreground">
              {live.alice.remoteCandidate}
            </div>
          ) : null}
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Bob ↔ SFU</div>
          <div className={`text-xl font-semibold tabular-nums ${rttColor(bobMs)}`}>
            {bobMs ?? '—'}
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              {bobMs != null ? 'ms' : ''}
            </span>
          </div>
          {live.bob.remoteCandidate ? (
            <div className="truncate font-mono text-[10px] text-muted-foreground">
              {live.bob.remoteCandidate}
            </div>
          ) : null}
        </div>
      </div>
      <div className="pt-1 text-[10px] text-muted-foreground">
        currentRoundTripTime from the selected ICE candidate pair (STUN binding RTT). This is the round-trip to the nearest Cloudflare edge — full client-to-client latency is ~2× this.
      </div>
    </div>
  )
}

function StatLine({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  )
}

function StatsCard({ title, stats }: { title: string; stats: MediaResult['stats']['aliceRx'] }) {
  if (stats.length === 0) {
    return (
      <div className="surface-card p-4 text-xs text-muted-foreground">
        {title}: no inbound tracks
      </div>
    )
  }
  return (
    <div className="surface-card space-y-2 p-4">
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        {title}
      </div>
      {stats.map((s, i) => (
        <div key={i} className="space-y-1">
          <div className="text-xs font-mono">{s.kind}</div>
          <StatLine label="packets" value={s.packets.toLocaleString()} />
          {typeof s.frames === 'number' ? <StatLine label="frames decoded" value={s.frames.toLocaleString()} /> : null}
          {typeof s.fps === 'number' ? <StatLine label="fps" value={s.fps.toFixed(0)} /> : null}
          {s.resolution ? <StatLine label="resolution" value={s.resolution} /> : null}
          <StatLine label="bitrate" value={`${s.kbps} kbps`} />
        </div>
      ))}
    </div>
  )
}

export function MediaTestSection({ basePath, push }: Props) {
  const [mode, setMode] = useState<Mode>('audio')
  const [running, setRunning] = useState<Mode | null>(null)
  const [active, setActive] = useState<ActiveTest | null>(null)
  const [liveStats, setLiveStats] = useState<MediaResult['stats']>({ aliceRx: [], bobRx: [] })
  const [liveRtt, setLiveRtt] = useState<LiveRtt>({ alice: { rttMs: null }, bob: { rttMs: null } })
  const [audioMuted, setAudioMuted] = useState(false)
  const [videoPaused, setVideoPaused] = useState(false)
  const [mediaEvents, setMediaEvents] = useState<ConsoleEvent[]>([])

  // Push to both the page-level console (visible at top) and the local
  // media-only console (visible below the test buttons). Useful when the
  // user has scrolled past the page-level console.
  const pushBoth: typeof push = (event) => {
    setMediaEvents((prev) => [...prev, { ...event, ts: Date.now() }])
    push(event)
  }
  const aliceLocalRef = useRef<HTMLVideoElement>(null)
  const bobRemoteRef = useRef<HTMLVideoElement>(null)
  const aliceRemoteRef = useRef<HTMLVideoElement>(null)
  const aliceLocalAudioRef = useRef<HTMLAudioElement>(null)

  // When an active test is set, wire its streams to the right media elements.
  useEffect(() => {
    if (!active) return

    const aliceLocal = active.session.alice.peer.getLocalStream()
    const aliceRemote = active.session.alice.peer.getRemoteStream()
    const bobRemote = active.session.bob.peer.getRemoteStream()

    if (active.mode === 'audio') {
      if (aliceLocalAudioRef.current && aliceLocal) aliceLocalAudioRef.current.srcObject = aliceLocal
    } else {
      if (aliceLocalRef.current && aliceLocal) aliceLocalRef.current.srcObject = aliceLocal
    }
    if (aliceRemoteRef.current && aliceRemote) aliceRemoteRef.current.srcObject = aliceRemote
    if (bobRemoteRef.current && bobRemote) bobRemoteRef.current.srcObject = bobRemote
  }, [active])

  // Live-poll getStats() and getRtt() every second so the cards reflect a
  // moving call, not a one-shot snapshot from the moment the test passed.
  useEffect(() => {
    if (!active) return
    let cancelled = false
    const interval = setInterval(async () => {
      try {
        const [aliceStats, bobStats, aliceRtt, bobRtt] = await Promise.all([
          active.session.alice.peer.getInboundStats(),
          active.session.bob.peer.getInboundStats(),
          active.session.alice.peer.getRtt(),
          active.session.bob.peer.getRtt(),
        ])
        if (cancelled) return
        const elapsedSec = (Date.now() - active.startedAt) / 1000
        setLiveStats({
          aliceRx: summarizeLive(aliceStats, elapsedSec),
          bobRx: summarizeLive(bobStats, elapsedSec),
        })
        setLiveRtt({ alice: aliceRtt, bob: bobRtt })
      } catch {
        // peer may have been closed mid-poll — ignore
      }
    }, 1000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [active])

  // Reset mute/pause UI state when test changes.
  useEffect(() => {
    setAudioMuted(false)
    setVideoPaused(false)
  }, [active])

  // Cleanup on unmount only (not on every active change — runScenario already
  // cleans the previous active before setting the new one). Capture latest
  // active in a ref so the cleanup function sees it.
  const activeRef = useRef(active)
  useEffect(() => {
    activeRef.current = active
  }, [active])
  useEffect(() => {
    return () => {
      void activeRef.current?.session.cleanup()
    }
  }, [])

  async function runScenario(target: Mode) {
    if (running) return
    if (active) {
      await active.session.cleanup()
      setActive(null)
    }
    setRunning(target)
    pushBoth({ level: 'info', text: `Starting media test: ${target}` })

    try {
      let outcome: { result: MediaResult; session: MediaSession }
      if (target === 'audio') outcome = await runAudioScenario({ basePath, push: pushBoth })
      else if (target === 'video') outcome = await runVideoScenario({ basePath, push: pushBoth })
      else outcome = await runScreenScenario({ basePath, push: pushBoth })

      if (!outcome.result.ok) {
        pushBoth({ level: 'error', text: `Media test "${target}" failed`, detail: outcome.result.error })
      } else {
        pushBoth({ level: 'success', text: `Media test "${target}" passed in ${outcome.result.durationMs}ms` })
      }
      setLiveStats(outcome.result.stats)
      setActive({ mode: target, session: outcome.session, result: outcome.result, startedAt: Date.now() })
    } catch (err) {
      pushBoth({ level: 'error', text: `Unexpected error: ${err instanceof Error ? err.message : String(err)}` })
    } finally {
      setRunning(null)
    }
  }

  async function endActive() {
    if (active) {
      pushBoth({ level: 'info', text: 'Stopping media test and leaving rooms.' })
      await active.session.cleanup()
      setActive(null)
      setLiveStats({ aliceRx: [], bobRx: [] })
      setLiveRtt({ alice: { rttMs: null }, bob: { rttMs: null } })
    }
  }

  function toggleAudio() {
    if (!active) return
    const next = !audioMuted
    active.session.alice.peer.setLocalTrackEnabled('audio', !next)
    setAudioMuted(next)
    pushBoth({ level: 'info', text: next ? 'Alice mic muted' : 'Alice mic unmuted' })
  }

  function toggleVideo() {
    if (!active) return
    const next = !videoPaused
    active.session.alice.peer.setLocalTrackEnabled('video', !next)
    setVideoPaused(next)
    pushBoth({ level: 'info', text: next ? 'Alice camera paused' : 'Alice camera resumed' })
  }

  const buttonIcon = (m: Mode) => (m === 'audio' ? <Mic className="h-3.5 w-3.5 mr-1.5" /> : m === 'video' ? <VideoIcon className="h-3.5 w-3.5 mr-1.5" /> : <MonitorSmartphone className="h-3.5 w-3.5 mr-1.5" />)
  const buttonLabel = (m: Mode) => (m === 'audio' ? 'Audio' : m === 'video' ? 'Audio + Video' : 'Screen share')

  return (
    <section className="surface-card space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-base font-semibold tracking-tight">Live media test</h2>
          <p className="text-sm text-muted-foreground">
            Two real WebRTC peers in this tab, publishing through the Cloudflare SFU and subscribing to each other.
          </p>
        </div>
        {active ? (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={active.result.ok ? 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400' : 'border-red-500/40 text-red-600 dark:text-red-400'}>
              {active.result.ok ? <><CheckCircle2 className="h-3 w-3 mr-1" /> {active.mode}: ok</> : <><XCircle className="h-3 w-3 mr-1" /> {active.mode}: failed</>}
            </Badge>
            <Button variant="outline" size="sm" onClick={endActive}>End test</Button>
          </div>
        ) : null}
      </div>

      <MediaConsole events={mediaEvents} onClear={() => setMediaEvents([])} />

      <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
        <TabsList className="grid grid-cols-3 w-fit">
          <TabsTrigger value="audio">Audio</TabsTrigger>
          <TabsTrigger value="video">Video</TabsTrigger>
          <TabsTrigger value="screen">Screen</TabsTrigger>
        </TabsList>

        {(['audio', 'video', 'screen'] as Mode[]).map((m) => (
          <TabsContent key={m} value={m} className="mt-4 space-y-4">
            <div className="flex items-center gap-3">
              <Button onClick={() => runScenario(m)} disabled={running !== null}>
                {running === m ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Running…</> : <><Play className="h-3.5 w-3.5 mr-1.5" /> Run {buttonLabel(m).toLowerCase()} test</>}
              </Button>
              <span className="text-xs text-muted-foreground flex items-center gap-1">{buttonIcon(m)}{buttonLabel(m)}</span>
            </div>

            {m === 'screen' ? (
              <div className="surface-card flex items-start gap-2 px-3 py-2 text-xs text-muted-foreground">
                <span className="mt-0.5 inline-block h-3 w-1 shrink-0 rounded-full bg-amber-500/60" aria-hidden />
                <span>
                  Your browser will prompt you to choose a window or screen to share. Bob (the receiver) will display it below.
                </span>
              </div>
            ) : null}

            {/* Media surfaces */}
            {active && active.mode === m ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {m === 'audio' ? (
                  <>
                    <div className="space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Alice — local mic</div>
                      <audio ref={aliceLocalAudioRef} autoPlay muted className="w-full" controls />
                    </div>
                    <div className="space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Bob — remote (Alice's mic via SFU)</div>
                      <audio ref={bobRemoteRef} autoPlay className="w-full" controls />
                    </div>
                  </>
                ) : m === 'video' ? (
                  <>
                    <div className="space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Alice — local camera</div>
                      <video ref={aliceLocalRef} autoPlay playsInline muted className="w-full rounded-md bg-black aspect-video" />
                    </div>
                    <div className="space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Bob — remote (Alice's camera via SFU)</div>
                      <video ref={bobRemoteRef} autoPlay playsInline className="w-full rounded-md bg-black aspect-video" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Alice — local screen</div>
                      <video ref={aliceLocalRef} autoPlay playsInline muted className="w-full rounded-md bg-black aspect-video" />
                    </div>
                    <div className="space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Bob — remote (Alice's screen via SFU)</div>
                      <video ref={bobRemoteRef} autoPlay playsInline className="w-full rounded-md bg-black aspect-video" />
                    </div>
                  </>
                )}
              </div>
            ) : null}

            {/* Mute / pause controls (audio + video tests only) */}
            {active && active.mode === m && (m === 'audio' || m === 'video') ? (
              <div className="flex items-center gap-2">
                <Button variant={audioMuted ? 'default' : 'outline'} size="sm" onClick={toggleAudio}>
                  {audioMuted ? <><MicOff className="h-3.5 w-3.5 mr-1.5" /> Unmute mic</> : <><Mic className="h-3.5 w-3.5 mr-1.5" /> Mute mic</>}
                </Button>
                {m === 'video' ? (
                  <Button variant={videoPaused ? 'default' : 'outline'} size="sm" onClick={toggleVideo}>
                    {videoPaused ? <><VideoOff className="h-3.5 w-3.5 mr-1.5" /> Resume camera</> : <><VideoIcon className="h-3.5 w-3.5 mr-1.5" /> Pause camera</>}
                  </Button>
                ) : null}
                <span className="text-xs text-muted-foreground ml-2">Toggles `track.enabled` on Alice's local stream — frames keep flowing through the SFU but become silence/black.</span>
              </div>
            ) : null}

            {/* Connect-time hero + setup timeline */}
            {active && active.mode === m && active.result.ok ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ConnectTimeHero result={active.result} />
                <RttCard live={liveRtt} />
              </div>
            ) : null}
            {active && active.mode === m && active.result.timeline.length > 0 ? (
              <TimelineCard timeline={active.result.timeline} totalMs={active.result.durationMs} />
            ) : null}

            {/* Stats — refreshed every second while the test is active */}
            {active && active.mode === m ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <StatsCard title="Alice receives" stats={liveStats.aliceRx} />
                <StatsCard title="Bob receives" stats={liveStats.bobRx} />
              </div>
            ) : null}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  )
}
